/* ============================================================
   Sectors — single source of truth for all 13 sector pages.
   slug must match a Cloudinary asset id `adel/sector-${slug}`.
   ============================================================ */

export const SECTORS = [
  {
    slug: "construction-infrastructure",
    name: "Construction & Infrastructure",
    short: "Construction",
    blurb: "Ready crews for the biggest builds. From foundations to fit-out.",
    intro: "We staff the steel, the formwork and the finishes. Every candidate is matched to your site and ready to work before their CV reaches you, and our paperwork passes the first time, every time.",
    roles: ["Welders (TIG/MIG)", "Steel fixers", "Shutter carpenters", "Masons & block layers", "Heavy equipment operators", "Plumbers", "Electricians", "Site supervisors", "Safety officers"],
    cities: ["Riyadh", "Dubai", "Doha", "Jeddah", "Dammam"],
    count: 1750,
  },
  {
    slug: "hospitality-catering",
    name: "Hospitality & Catering",
    short: "Hospitality",
    blurb: "Front-of-house and back-of-house, ready for premium service standards.",
    intro: "From 5-star hotels to high-volume catering, we staff the people who make hospitality feel effortless. Fluent in English, service-ready, presentation-ready.",
    roles: ["F&B service crew", "Commis chefs", "Kitchen stewards", "Housekeeping supervisors", "Room attendants", "Banquet servers", "Baristas", "Bell staff", "Pastry assistants"],
    cities: ["Doha", "Dubai", "Madinah", "Manama"],
    count: 980,
  },
  {
    slug: "healthcare-caregiving",
    name: "Healthcare & Caregiving",
    short: "Healthcare",
    blurb: "Caregivers and clinical support staff, ready to provide dependable care.",
    intro: "Nursing aides, caregivers, lab assistants — matched to the clinical setting and briefed on the cultural context, ready to work from day one.",
    roles: ["Nursing aides", "Caregivers (elderly)", "Caregivers (children)", "Lab assistants", "Patient attendants", "Pharmacy technicians", "Medical secretaries"],
    cities: ["Riyadh", "Abu Dhabi", "Manama"],
    count: 420,
  },
  {
    slug: "logistics-warehousing",
    name: "Logistics & Warehousing",
    short: "Logistics",
    blurb: "Forklift-certified crews and warehouse supervisors who keep ops moving.",
    intro: "Order pickers, forklift operators, warehouse supervisors — matched to your gear and your floor, so day-one productivity is real.",
    roles: ["Forklift operators", "Reach truck operators", "Order pickers & packers", "Warehouse supervisors", "Inventory clerks", "Loaders & sorters", "Fleet dispatchers"],
    cities: ["Jebel Ali (Dubai)", "Dammam", "Sharjah", "Kuwait City"],
    count: 540,
  },
  {
    slug: "manufacturing-industrial",
    name: "Manufacturing & Industrial",
    short: "Manufacturing",
    blurb: "Production line and industrial trades, hands ready for shift work.",
    intro: "Production operators, line supervisors, machinists. Reliable for shift work, prepared for industrial safety standards.",
    roles: ["Production operators", "Machine operators", "Line supervisors", "Quality control inspectors", "Machinists", "Industrial cleaners", "Packagers"],
    cities: ["Sharjah", "Jeddah", "Manama"],
    count: 390,
  },
  {
    slug: "security-services",
    name: "Security Services",
    short: "Security",
    blurb: "SIRA-eligible security officers, trained and ready for the role.",
    intro: "Security officers trained and ready — from corporate sites to events. We handle SIRA-equivalent paperwork at origin so deployment isn't delayed.",
    roles: ["Security officers", "Site supervisors", "CCTV operators", "Event security", "Concierge security"],
    cities: ["Doha", "Dubai", "Abu Dhabi"],
    count: 260,
  },
  {
    slug: "facilities-management",
    name: "Facilities Management",
    short: "Facilities",
    blurb: "Building operations crews — soft and hard FM, both covered.",
    intro: "FM technicians, building services staff, soft-FM teams. We staff the people who keep buildings running quietly behind the scenes.",
    roles: ["FM technicians", "HVAC technicians", "Plumbers (FM)", "Electricians (FM)", "Cleaners (commercial)", "Pest control", "Landscapers"],
    cities: ["Dubai", "Doha", "Riyadh"],
    count: 320,
  },
  {
    slug: "retail-commercial-operations",
    name: "Retail & Commercial Operations",
    short: "Retail",
    blurb: "Sales associates and store crews trained for premium-brand standards.",
    intro: "Retail floor staff, store supervisors, visual merchandising assistants — matched for service orientation and English proficiency.",
    roles: ["Sales associates", "Store supervisors", "Cashiers", "Visual merchandisers", "Stockroom assistants"],
    cities: ["Dubai", "Riyadh", "Doha"],
    count: 210,
  },
  {
    slug: "maintenance-technical-services",
    name: "Maintenance & Technical Services",
    short: "Maintenance",
    blurb: "Technical maintenance crews for industrial, commercial and residential.",
    intro: "Mechanical and electrical maintenance, HVAC, lift technicians — certified and ready, with local equivalency arranged before placement.",
    roles: ["Mechanical fitters", "Electricians", "HVAC technicians", "Lift technicians", "Plumbing technicians"],
    cities: ["Muscat", "Dubai", "Riyadh"],
    count: 180,
  },
  {
    slug: "cleaning-housekeeping",
    name: "Cleaning & Housekeeping",
    short: "Cleaning",
    blurb: "Commercial cleaners and housekeeping crews trained to international standards.",
    intro: "Hotel housekeeping, commercial cleaning, deep-clean specialists. Paperwork done, briefed on the standards expected, ready to start.",
    roles: ["Room attendants", "Public area cleaners", "Laundry attendants", "Deep-clean specialists", "Window cleaners"],
    cities: ["Dubai", "Doha", "Manama"],
    count: 280,
  },
  {
    slug: "agriculture-farm-work",
    name: "Agriculture & Farm Work",
    short: "Agriculture",
    blurb: "Farm operators and greenhouse hands for large-scale agricultural projects.",
    intro: "Greenhouse staff, farm equipment operators, irrigation technicians. Practical hands with experience in commercial-scale agriculture.",
    roles: ["Greenhouse operators", "Farm equipment operators", "Irrigation technicians", "Livestock handlers", "Field supervisors"],
    cities: ["Riyadh (greater)", "Madinah", "Dammam"],
    count: 95,
  },
  {
    slug: "domestic-support-services",
    name: "Domestic Support Services",
    short: "Domestic Support",
    blurb: "Trained domestic helpers placed through legitimate, transparent channels.",
    intro: "Domestic workers placed only via the official frameworks of each destination country. Pre-departure cultural and language briefing included.",
    roles: ["Nannies", "Eldercare assistants", "Drivers (private)", "Household cooks", "Gardeners"],
    cities: ["Riyadh", "Doha", "Manama"],
    count: 220,
  },
  {
    slug: "transportation",
    name: "Transportation",
    short: "Transportation",
    blurb: "Commercial drivers and fleet operators, properly licensed.",
    intro: "Light, heavy and articulated drivers — properly licensed, with GCC equivalency arranged where required.",
    roles: ["Light vehicle drivers", "Heavy truck drivers", "Articulated drivers", "Fleet supervisors", "Bus drivers"],
    cities: ["Dubai", "Jeddah", "Riyadh"],
    count: 165,
  },
  {
    slug: "oil-gas-support",
    name: "Oil & Gas Support",
    short: "Oil & Gas",
    blurb: "Support crews for upstream, midstream and downstream operations.",
    intro: "Roustabouts, riggers, instrumentation techs, plant cleaners. Trained in industrial safety standards before deployment.",
    roles: ["Roustabouts", "Pipeline assistants", "Riggers", "Plant cleaners", "Instrumentation assistants"],
    cities: ["Dammam", "Muscat", "Doha"],
    count: 140,
  },
  {
    slug: "food-processing",
    name: "Food Processing",
    short: "Food Processing",
    blurb: "Production line and processing crews trained on hygiene standards.",
    intro: "HACCP-aware production operators, quality inspectors and packagers. Reliable for shift work.",
    roles: ["Production operators", "Quality inspectors", "Packagers", "Hygiene specialists", "Cold-chain handlers"],
    cities: ["Dubai", "Riyadh"],
    count: 90,
  },
  {
    slug: "marine-port",
    name: "Marine & Port",
    short: "Marine & Port",
    blurb: "Port crews, container handlers, and marine support staff.",
    intro: "Container terminal hands, port logistics crew, and marine support staff for the region's busiest ports.",
    roles: ["Container handlers", "Crane signallers", "Stevedores", "Marine deck hands", "Port logistics clerks"],
    cities: ["Jebel Ali (Dubai)", "Dammam", "Salalah"],
    count: 75,
  },
  {
    slug: "office-administration",
    name: "Office Administration",
    short: "Admin",
    blurb: "Bilingual office and admin support, accurate and ready to start.",
    intro: "Office assistants, secretaries, receptionists and document controllers. Fluent in English, comfortable with MS Office, ready to start.",
    roles: ["Office assistants", "Receptionists", "Document controllers", "Data entry clerks", "Bilingual secretaries"],
    cities: ["Dubai", "Riyadh", "Doha"],
    count: 55,
  },
];

export function getSectorBySlug(slug) {
  return SECTORS.find((s) => s.slug === slug);
}
